// Pure logic for the TSP Forum: topic types, filters, sorting, validation,
// and the API <-> UI shape adapters. No React/I/O so it is unit-tested; the
// backend validates and authorizes independently (this file trusts nothing
// it can't re-derive — capabilities always come from the server response).

export const FORUM_TYPES = [
  { value: "DISCUSSION", label: "Discussion" },
  { value: "QUESTION", label: "Question" },
];

export const TOPIC_FILTERS = [
  { value: "all", label: "All" },
  { value: "QUESTION", label: "Questions" },
  { value: "DISCUSSION", label: "Discussions" },
  { value: "unanswered", label: "Unanswered" },
  { value: "subscribed", label: "Subscribed" },
];

export const FORUM_SORTS = [
  { value: "latest", label: "Latest activity" },
  { value: "popular", label: "Most popular" },
];

export const TOPIC_PAGE_SIZE = 8;
export const TOPIC_TITLE_MIN = 8;
export const TOPIC_TITLE_MAX = 150;
export const TOPIC_BODY_MIN = 10;
export const TOPIC_BODY_MAX = 3000;
export const REPLY_MIN = 2;
export const REPLY_MAX = 2000;

// Top-to-bottom on screen — used to focus the first invalid field.
export const TOPIC_FIELD_ORDER = ["title", "category", "type", "body"];

export function validateTopic({ title = "", category = "", type = "", body = "" }, categories) {
  const errors = {};
  const cleanTitle = title.trim();
  const cleanBody = body.trim();

  if (!cleanTitle) errors.title = "Enter a title.";
  else if (cleanTitle.length < TOPIC_TITLE_MIN) errors.title = `Title must be at least ${TOPIC_TITLE_MIN} characters.`;
  else if (cleanTitle.length > TOPIC_TITLE_MAX) errors.title = `Title must be ${TOPIC_TITLE_MAX} characters or fewer.`;

  if (!category) errors.category = "Choose a category.";
  else if (!categories.some((entry) => entry.key === category)) errors.category = "Choose a category from the list.";

  if (!type) errors.type = "Choose whether this is a question or a discussion.";
  else if (!FORUM_TYPES.some((entry) => entry.value === type)) errors.type = "Choose a valid type.";

  if (!cleanBody) errors.body = "Add some details.";
  else if (cleanBody.length < TOPIC_BODY_MIN) errors.body = `Add at least ${TOPIC_BODY_MIN} characters.`;
  else if (cleanBody.length > TOPIC_BODY_MAX) errors.body = `Keep it under ${TOPIC_BODY_MAX} characters.`;

  return errors;
}

export function validateReply(text = "") {
  const clean = text.trim();
  if (!clean) return "Write a reply first.";
  if (clean.length < REPLY_MIN) return `Reply must be at least ${REPLY_MIN} characters.`;
  if (clean.length > REPLY_MAX) return `Keep replies under ${REPLY_MAX} characters.`;
  return "";
}

// ---- API <-> UI shape --------------------------------------------------------
// The server is authoritative for search/filter/sort/pagination and for every
// "may I ..." decision (topic.capabilities / reply.capabilities); these
// adapters only rename fields so the existing components (TopicRow,
// TopicDetail, TopicFormModal) keep working — they never re-derive a
// permission themselves.

export function adaptCategory(category) {
  return { key: category.key, label: category.label, tone: "neutral", count: category.topic_count };
}

export function adaptReply(reply) {
  return {
    id: reply.id,
    author: reply.author?.name || "Unknown",
    createdAt: reply.created_at.slice(0, 10),
    body: reply.body,
    helpful: reply.helpful_count,
    helpfulByMe: reply.helpful_by_me,
    best: reply.is_best_answer,
    capabilities: reply.capabilities,
  };
}

export function adaptTopic(topic) {
  return {
    id: topic.id,
    category: topic.category.key,
    type: topic.topic_type === "QUESTION" ? "question" : "discussion",
    title: topic.title,
    body: topic.body,
    author: topic.author?.name || "Unknown",
    createdAt: topic.created_at.slice(0, 10),
    lastActivityAt: (topic.last_activity_at || topic.created_at).slice(0, 10),
    pinned: topic.is_pinned,
    views: topic.view_count,
    likes: topic.like_count,
    likedByMe: topic.liked_by_me,
    subscribed: topic.subscribed_by_me,
    reported: Boolean(topic.is_reported),
    solved: Boolean(topic.has_best_answer),
    replies: (topic.replies || []).map(adaptReply),
    capabilities: topic.capabilities,
  };
}

// UI filter state -> GET /api/tsp/forum/topics query params.
export function topicListParams({ search, category, filter, sort, page, limit = TOPIC_PAGE_SIZE }) {
  const params = { sort, page, limit };
  if (search) params.search = search;
  if (category) params.category = category;
  if (filter === "QUESTION" || filter === "DISCUSSION") params.type = filter;
  else if (filter === "unanswered") params.unanswered = true;
  else if (filter === "subscribed") params.subscribed = true;
  return params;
}
